import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';

@Component({
  selector: 'app-sustainable-chemicals',
  standalone: true,
  imports: [CommonModule, RouterModule, ReactiveFormsModule],
  template: `
    <!-- Hero Section -->
    <section class="relative bg-gradient-to-br from-slate-900 to-blue-900 text-white py-16 md:py-24">
      <div class="absolute inset-0 bg-pattern opacity-20"></div>
      <div class="container mx-auto px-4 relative z-10">
        <div class="flex flex-col md:flex-row items-center">
          <div class="md:w-1/2 mb-10 md:mb-0">
            <div class="inline-flex items-center space-x-2 px-4 py-1.5 rounded-full bg-white/15 backdrop-blur-sm text-sm font-medium mb-6">
              <span class="w-2 h-2 rounded-full bg-blue-400 animate-pulse"></span>
              <span>ESG Intelligence</span>
            </div>
            <h1 class="text-3xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              Sustainable Chemicals
            </h1>
            <p class="text-xl md:text-2xl mb-8 text-blue-100">
              Transform your chemical manufacturing with AI-powered sustainability insights, ESG compliance tools, and process optimization solutions that reduce environmental impact while improving operational efficiency.
            </p>
            <div class="flex flex-col sm:flex-row gap-4">
              <a href="#signup" class="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg transition duration-300 text-center">
                Start Free Trial
              </a>
              <a href="#benefits" class="bg-transparent hover:bg-white/10 border border-white text-white font-bold py-3 px-6 rounded-lg transition duration-300 text-center">
                Learn More
              </a>
            </div>
          </div>
          <div class="md:w-1/2">
            <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 shadow-2xl">
              <img src="https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?auto=format&fit=crop&q=80&w=1000" 
                   alt="Sustainable Chemicals Dashboard showing environmental metrics and sustainability KPIs" class="rounded-lg w-full">
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Signup Form Section -->
    <section class="py-16 bg-slate-50" id="signup">
      <div class="container mx-auto px-4">
        <div class="max-w-6xl mx-auto">
          <div class="text-center mb-10">
            <h2 class="text-3xl md:text-4xl font-bold mb-4 text-slate-900">Stay Ahead with Industry Intelligence</h2>
            <p class="text-xl text-slate-600 max-w-3xl mx-auto">
              Subscribe to our newsletter for critical insights on sustainable chemical industry trends
            </p>
          </div>
          
          <div class="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div class="grid grid-cols-1 lg:grid-cols-5">
              <!-- Left side - Benefits -->
              <div class="lg:col-span-2 bg-slate-800 text-white p-8 lg:p-10 relative">
                <div class="absolute top-0 right-0 w-32 h-32 bg-blue-500 rounded-bl-full opacity-20"></div>
                <div class="relative z-10">
                  <h3 class="text-2xl font-bold mb-6">Free 30-Day Trial</h3>
                  <p class="mb-8">
                    Industry Intelligence's sustainable chemical industry newsletter covers critical news and market drivers. After the trial, continue for a low monthly fee or opt out with no obligation.
                  </p>
                  
                  <ul class="space-y-4">
                    <li class="flex items-start">
                      <div class="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center mr-3 mt-1">
                        <i class="fas fa-check text-white text-sm"></i>
                      </div>
                      <p>Daily updates on sustainable innovations</p>
                    </li>
                    <li class="flex items-start">
                      <div class="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center mr-3 mt-1">
                        <i class="fas fa-check text-white text-sm"></i>
                      </div>
                      <p>Manage complex regulatory information</p>
                    </li>
                    <li class="flex items-start">
                      <div class="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center mr-3 mt-1">
                        <i class="fas fa-check text-white text-sm"></i>
                      </div>
                      <p>Eliminate surprises with proactive insights</p>
                    </li>
                    <li class="flex items-start">
                      <div class="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center mr-3 mt-1">
                        <i class="fas fa-check text-white text-sm"></i>
                      </div>
                      <p>360-degree market monitoring system</p>
                    </li>
                  </ul>
                  
                  <div class="mt-8 pt-6 border-t border-slate-700">
                    <a href="#" class="text-blue-300 hover:text-white flex items-center">
                      <i class="fas fa-file-alt mr-2"></i>
                      View a sample report before signing up
                    </a>
                  </div>
                </div>
              </div>
              
              <!-- Right side - Form -->
              <div class="lg:col-span-3 p-8 lg:p-10">
                <h3 class="text-2xl font-bold mb-6 text-slate-900">Sign up for the Sustainable Chemicals Report</h3>
                
                <form [formGroup]="signupForm" (ngSubmit)="onSubmit()" class="space-y-6">
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label for="firstName" class="block text-sm font-medium text-slate-700 mb-1">First name</label>
                      <input type="text" id="firstName" formControlName="firstName" 
                             class="w-full px-4 py-3 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                             placeholder="First">
                      <div *ngIf="signupForm.get('firstName')?.invalid && signupForm.get('firstName')?.touched" class="text-red-500 text-sm mt-1">
                        First name is required
                      </div>
                    </div>
                    <div>
                      <label for="lastName" class="block text-sm font-medium text-slate-700 mb-1">Last name</label>
                      <input type="text" id="lastName" formControlName="lastName" 
                             class="w-full px-4 py-3 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                             placeholder="Last">
                      <div *ngIf="signupForm.get('lastName')?.invalid && signupForm.get('lastName')?.touched" class="text-red-500 text-sm mt-1">
                        Last name is required
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <label for="email" class="block text-sm font-medium text-slate-700 mb-1">Work email</label>
                    <input type="email" id="email" formControlName="email" 
                           class="w-full px-4 py-3 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                           placeholder="you@company.com">
                    <div *ngIf="signupForm.get('email')?.invalid && signupForm.get('email')?.touched" class="text-red-500 text-sm mt-1">
                      Please enter a valid email address
                    </div>
                  </div>
                  
                  <div class="pt-4">
                    <button type="submit" [disabled]="signupForm.invalid"
                            class="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-6 rounded-lg transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg">
                      Start Your Free 30-Day Trial
                    </button>
                  </div>
                  
                  <div class="flex items-center justify-center space-x-4 pt-4">
                    <span class="h-px bg-slate-300 flex-grow"></span>
                    <span class="text-slate-500 text-sm font-medium">OR</span>
                    <span class="h-px bg-slate-300 flex-grow"></span>
                  </div>
                  
                  <div class="grid grid-cols-3 gap-4">
                    <button type="button" class="flex justify-center items-center py-3 px-4 border border-slate-300 rounded-lg hover:bg-slate-50 transition duration-300">
                      <i class="fab fa-google text-red-500 mr-2"></i>
                      <span class="text-slate-700">Google</span>
                    </button>
                    <button type="button" class="flex justify-center items-center py-3 px-4 border border-slate-300 rounded-lg hover:bg-slate-50 transition duration-300">
                      <i class="fab fa-linkedin text-blue-600 mr-2"></i>
                      <span class="text-slate-700">LinkedIn</span>
                    </button>
                    <button type="button" class="flex justify-center items-center py-3 px-4 border border-slate-300 rounded-lg hover:bg-slate-50 transition duration-300">
                      <i class="fab fa-apple text-slate-800 mr-2"></i>
                      <span class="text-slate-700">Apple</span>
                    </button>
                  </div>
                  
                  <div class="pt-6 text-sm text-slate-500 border-t border-slate-200 mt-6">
                    <p class="flex items-center">
                      <i class="fas fa-lock text-slate-400 mr-2"></i>
                      <span>At Industry Intelligence, we value your privacy. We will never sell or share your personal information.</span>
                    </p>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Combined Benefits & Features Section -->
    <section class="py-16 bg-white" id="benefits">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold mb-4 text-slate-900">Sustainable Chemicals Intelligence Benefits</h2>
          <p class="text-xl text-slate-600 max-w-3xl mx-auto">
            Get comprehensive coverage of the sustainable chemicals industry with our expert insights
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          <div class="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition duration-300 border border-slate-100">
            <div class="text-blue-600 text-4xl mb-4">
              <i class="fas fa-newspaper"></i>
            </div>
            <h3 class="text-xl font-bold mb-3 text-slate-900">Relevant News & Analysis</h3>
            <p class="text-slate-600">Daily updates on sustainable innovations, best practices in material recyclability and reuse, and key environmental initiatives across the industry.</p>
          </div>
          
          <div class="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition duration-300 border border-slate-100">
            <div class="text-blue-600 text-4xl mb-4">
              <i class="fas fa-brain"></i>
            </div>
            <h3 class="text-xl font-bold mb-3 text-slate-900">Manage Complex Information</h3>
            <p class="text-slate-600">The chemical sector is increasingly scrutinized by both the public and regulators over its environmental impacts. Prioritizing sustainable practices is crucial for maintaining a competitive edge.</p>
          </div>
          
          <div class="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition duration-300 border border-slate-100">
            <div class="text-blue-600 text-4xl mb-4">
              <i class="fas fa-shield-alt"></i>
            </div>
            <h3 class="text-xl font-bold mb-3 text-slate-900">Eliminate Surprises</h3>
            <p class="text-slate-600">By subscribing to the Sustainable Chemicals Report, you'll receive insights to stay ahead of the curve and drive meaningful progress toward your company's goals.</p>
          </div>
          
          <div class="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition duration-300 border border-slate-100">
            <div class="text-blue-600 text-4xl mb-4">
              <i class="fas fa-globe"></i>
            </div>
            <h3 class="text-xl font-bold mb-3 text-slate-900">360-Degree Market Watch</h3>
            <p class="text-slate-600">Our proprietary 'Always On' monitoring system tracks evolving market news as it happens, giving you a 360-degree view of your business landscape.</p>
          </div>
          
          <div class="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition duration-300 border border-slate-100">
            <div class="text-blue-600 text-4xl mb-4">
              <i class="fas fa-chart-pie"></i>
            </div>
            <h3 class="text-xl font-bold mb-3 text-slate-900">Market Analysis</h3>
            <p class="text-slate-600">Expert analysis of market trends, demand patterns, and competitive landscapes to help you make informed business decisions and identify new opportunities.</p>
          </div>
          
          <div class="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition duration-300 border border-slate-100">
            <div class="text-blue-600 text-4xl mb-4">
              <i class="fas fa-balance-scale"></i>
            </div>
            <h3 class="text-xl font-bold mb-3 text-slate-900">Regulatory Updates</h3>
            <p class="text-slate-600">Timely information on changing regulations, compliance requirements, and policy developments affecting the sustainable chemicals industry.</p>
          </div>
        </div>
        
        <div class="max-w-4xl mx-auto bg-slate-50 rounded-xl p-8 shadow-md">
          <div class="flex flex-col md:flex-row items-center">
            <div class="md:w-1/3 mb-6 md:mb-0 text-center">
              <img src="https://images.unsplash.com/photo-1581093450021-4a7360e9a6b5?auto=format&fit=crop&q=80&w=400" 
                   alt="Newsletter Sample" class="rounded-lg shadow-md mx-auto">
            </div>
            <div class="md:w-2/3 md:pl-10">
              <h3 class="text-2xl font-bold mb-4 text-slate-900">What Our Subscribers Say</h3>
              <div class="mb-6">
                <div class="text-blue-600 text-xl mb-2">
                  <i class="fas fa-quote-left"></i>
                </div>
                <p class="text-slate-700 italic mb-4">"The Sustainable Chemicals newsletter has become an essential part of my weekly routine. The insights and analysis help me stay ahead of industry trends and make informed decisions."</p>
                <div class="flex items-center">
                  <div class="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold mr-3">
                    M
                  </div>
                  <div>
                    <p class="font-bold text-slate-900">Michael Reynolds</p>
                    <p class="text-slate-600 text-sm">Chief Sustainability Officer, Global Chemical Solutions</p>
                  </div>
                </div>
              </div>
              <div class="mt-6 pt-6 border-t border-slate-200">
                <a href="#signup" class="inline-block bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg transition duration-300">
                  Join Our Subscribers
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Combined Newsletter Value Section -->
    <section class="py-16 bg-white">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold mb-4 text-slate-900">Sustainable Chemicals Intelligence Features</h2>
          <p class="text-xl text-slate-600 max-w-3xl mx-auto">
            Comprehensive insights and measurable impact for chemical industry professionals
          </p>
        </div>

        <!-- Top metrics row -->
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-16">
          <div class="bg-slate-50 rounded-xl p-6 text-center transform transition duration-300 hover:scale-105 hover:shadow-md">
            <div class="text-blue-600 text-3xl mb-2">
              <i class="fas fa-clock"></i>
            </div>
            <div class="text-3xl font-bold text-slate-900">30%</div>
            <p class="text-slate-600 font-medium">Time Saved on Research</p>
          </div>
          <div class="bg-slate-50 rounded-xl p-6 text-center transform transition duration-300 hover:scale-105 hover:shadow-md">
            <div class="text-blue-600 text-3xl mb-2">
              <i class="fas fa-lightbulb"></i>
            </div>
            <div class="text-3xl font-bold text-slate-900">25+</div>
            <p class="text-slate-600 font-medium">Weekly Insights</p>
          </div>
          <div class="bg-slate-50 rounded-xl p-6 text-center transform transition duration-300 hover:scale-105 hover:shadow-md">
            <div class="text-blue-600 text-3xl mb-2">
              <i class="fas fa-chart-line"></i>
            </div>
            <div class="text-3xl font-bold text-slate-900">40%</div>
            <p class="text-slate-600 font-medium">Decision Confidence</p>
          </div>
          <div class="bg-slate-50 rounded-xl p-6 text-center transform transition duration-300 hover:scale-105 hover:shadow-md">
            <div class="text-blue-600 text-3xl mb-2">
              <i class="fas fa-users"></i>
            </div>
            <div class="text-3xl font-bold text-slate-900">1000+</div>
            <p class="text-slate-600 font-medium">Active Subscribers</p>
          </div>
        </div>

        <!-- Content tabs -->
        <div class="max-w-6xl mx-auto">
          <div class="bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-100">
            <!-- Tab headers -->
            <div class="flex flex-wrap border-b border-slate-200">
              <div class="w-1/2 md:w-1/3 text-center py-4 px-4 bg-blue-50 border-b-2 border-blue-600">
                <span class="font-bold text-blue-800">What You'll Receive</span>
              </div>
              <div class="w-1/2 md:w-1/3 text-center py-4 px-4 bg-white">
                <span class="font-medium text-slate-500">How It Works</span>
              </div>
              <div class="hidden md:block md:w-1/3 text-center py-4 px-4 bg-white">
                <span class="font-medium text-slate-500">Success Stories</span>
              </div>
            </div>
            
            <!-- Tab content -->
            <div class="p-6 md:p-8">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                <!-- Left column -->
                <div>
                  <h3 class="text-xl font-bold mb-6 text-slate-900">Core Newsletter Content</h3>
                  
                  <div class="space-y-6">
                    <div class="flex items-start">
                      <div class="flex-shrink-0 w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center mr-4">
                        <i class="fas fa-newspaper text-blue-600 text-xl"></i>
                      </div>
                      <div>
                        <h4 class="font-bold text-slate-900 mb-1">Industry News & Analysis</h4>
                        <p class="text-slate-600">Daily updates on sustainable innovations, best practices in material recyclability and reuse, and key environmental initiatives.</p>
                      </div>
                    </div>
                    
                    <div class="flex items-start">
                      <div class="flex-shrink-0 w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center mr-4">
                        <i class="fas fa-chart-pie text-blue-600 text-xl"></i>
                      </div>
                      <div>
                        <h4 class="font-bold text-slate-900 mb-1">Market Intelligence</h4>
                        <p class="text-slate-600">Expert analysis of market trends, demand patterns, and competitive landscapes to help you make informed business decisions.</p>
                      </div>
                    </div>
                    
                    <div class="flex items-start">
                      <div class="flex-shrink-0 w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center mr-4">
                        <i class="fas fa-balance-scale text-blue-600 text-xl"></i>
                      </div>
                      <div>
                        <h4 class="font-bold text-slate-900 mb-1">Regulatory Updates</h4>
                        <p class="text-slate-600">Timely information on changing regulations, compliance requirements, and policy developments affecting the industry.</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- Right column -->
                <div>
                  <h3 class="text-xl font-bold mb-6 text-slate-900">Strategic Benefits</h3>
                  
                  <div class="space-y-6">
                    <div class="flex items-start">
                      <div class="flex-shrink-0 w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center mr-4">
                        <i class="fas fa-brain text-blue-600 text-xl"></i>
                      </div>
                      <div>
                        <h4 class="font-bold text-slate-900 mb-1">Complex Information, Simplified</h4>
                        <p class="text-slate-600">We distill complex regulatory and market information into clear, actionable insights that save you time and resources.</p>
                      </div>
                    </div>
                    
                    <div class="flex items-start">
                      <div class="flex-shrink-0 w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center mr-4">
                        <i class="fas fa-shield-alt text-blue-600 text-xl"></i>
                      </div>
                      <div>
                        <h4 class="font-bold text-slate-900 mb-1">Proactive Risk Management</h4>
                        <p class="text-slate-600">Stay ahead of industry shifts and regulatory changes that could impact your business, eliminating costly surprises.</p>
                      </div>
                    </div>
                    
                    <div class="flex items-start">
                      <div class="flex-shrink-0 w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center mr-4">
                        <i class="fas fa-globe text-blue-600 text-xl"></i>
                      </div>
                      <div>
                        <h4 class="font-bold text-slate-900 mb-1">360° Market Visibility</h4>
                        <p class="text-slate-600">Our 'Always On' monitoring system tracks evolving market news as it happens, giving you complete business landscape visibility.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Sample report preview -->
              <div class="mt-12 pt-8 border-t border-slate-200">
                <div class="flex flex-col md:flex-row items-center">
                  <div class="md:w-1/3 mb-6 md:mb-0">
                    <img src="https://images.unsplash.com/photo-1581093450021-4a7360e9a6b5?auto=format&fit=crop&q=80&w=400" 
                         alt="Newsletter Sample" class="rounded-lg shadow-md mx-auto">
                  </div>
                  <div class="md:w-2/3 md:pl-10">
                    <h3 class="text-xl font-bold mb-3 text-slate-900">See What You'll Get</h3>
                    <p class="text-slate-600 mb-6">
                      Our newsletter delivers concise, actionable intelligence that helps you navigate the complex sustainable chemicals landscape. View a sample report to see the quality and depth of insights you'll receive.
                    </p>
                    <div class="flex flex-col sm:flex-row gap-4">
                      <a href="#" class="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition duration-300">
                        <i class="fas fa-file-alt mr-2"></i>
                        View Sample Report
                      </a>
                      <a href="#signup" class="inline-flex items-center justify-center bg-slate-100 hover:bg-slate-200 text-slate-800 font-medium py-3 px-6 rounded-lg transition duration-300">
                        <i class="fas fa-arrow-right mr-2"></i>
                        Start Free Trial
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ & Final CTA Section -->
    <section class="py-16 bg-slate-50" id="faq">
      <div class="container mx-auto px-4">
        <div class="max-w-6xl mx-auto">
          <div class="text-center mb-12">
            <h2 class="text-3xl md:text-4xl font-bold mb-4 text-slate-900">Frequently Asked Questions</h2>
            <p class="text-xl text-slate-600 max-w-3xl mx-auto">
              Get answers to common questions about our Sustainable Chemicals newsletter
            </p>
          </div>

          <div class="grid grid-cols-1 lg:grid-cols-5 gap-8 mb-16">
            <div class="lg:col-span-3">
              <div class="space-y-6">
                <div class="bg-white rounded-xl p-6 shadow-md">
                  <h3 class="text-xl font-bold mb-3 text-slate-900">How often will I receive the newsletter?</h3>
                  <p class="text-slate-600">The Sustainable Chemicals newsletter is delivered weekly, typically on Monday mornings, ensuring you start your week with the latest industry insights.</p>
                </div>
                
                <div class="bg-white rounded-xl p-6 shadow-md">
                  <h3 class="text-xl font-bold mb-3 text-slate-900">What happens after my 30-day free trial?</h3>
                  <p class="text-slate-600">After your free trial ends, you can choose to continue your subscription for a low monthly fee or opt out with no obligation. We'll send you a reminder before your trial expires.</p>
                </div>
                
                <div class="bg-white rounded-xl p-6 shadow-md">
                  <h3 class="text-xl font-bold mb-3 text-slate-900">Can I share the newsletter with my colleagues?</h3>
                  <p class="text-slate-600">Individual subscriptions are for single-user access only. We offer corporate subscriptions for teams that want to share the newsletter across multiple team members. Contact us for corporate pricing.</p>
                </div>
              </div>
            </div>
            
            <div class="lg:col-span-2">
              <div class="bg-white rounded-xl p-8 shadow-md h-full">
                <div class="space-y-6">
                  <div class="bg-white rounded-xl p-6 shadow-md">
                    <h3 class="text-xl font-bold mb-3 text-slate-900">How much does the subscription cost after the trial?</h3>
                    <p class="text-slate-600">After your free trial, the subscription costs $49 per month for individual users. We also offer annual subscriptions at a discounted rate of $470 per year (saving you two months).</p>
                  </div>
                  
                  <div class="bg-white rounded-xl p-6 shadow-md">
                    <h3 class="text-xl font-bold mb-3 text-slate-900">Can I cancel my subscription at any time?</h3>
                    <p class="text-slate-600">Yes, you can cancel your subscription at any time. There are no long-term commitments or cancellation fees. Simply log into your account or contact our customer service team to cancel.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Final CTA -->
          <div class="bg-gradient-to-r from-slate-800 to-blue-800 rounded-2xl overflow-hidden shadow-xl">
            <div class="grid grid-cols-1 md:grid-cols-2">
              <div class="p-8 md:p-12 flex items-center">
                <div>
                  <h3 class="text-2xl md:text-3xl font-bold mb-4 text-white">Ready to Stay Ahead of Industry Trends?</h3>
                  <p class="text-lg text-slate-200 mb-8">
                    Join industry leaders who rely on our Sustainable Chemicals newsletter for critical insights and information.
                  </p>
                  <a href="#signup" class="inline-block bg-white text-blue-800 hover:bg-blue-50 font-bold py-4 px-8 rounded-lg transition duration-300 text-lg shadow-md">
                    Start Your Free 30-Day Trial
                  </a>
                </div>
              </div>
              <div class="hidden md:block relative">
                <div class="absolute inset-0 bg-pattern opacity-10"></div>
                <img src="https://images.unsplash.com/photo-1581093588401-fbb62a02f120?auto=format&fit=crop&q=80&w=800" 
                     alt="Sustainable Chemicals" class="h-full w-full object-cover">
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  `,
  styles: [`
    .bg-pattern {
      background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    }
  `]
})
export class SustainableChemicalsComponent {
  signupForm: FormGroup;
  
  constructor(private fb: FormBuilder) {
    this.signupForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]]
    });
  }
  
  onSubmit() {
    if (this.signupForm.valid) {
      console.log('Form submitted:', this.signupForm.value);
      // Add actual submission logic here
    }
  }
} 